import { useEffect, useRef, type ReactNode, type ComponentType } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, ChevronLeft } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { cn } from "@/lib/utils";

/* ─────────────────────────────────────────────────────────────
   Gerdoo — Apple-style PaaS/IaaS design primitives.
   White / canvas / black bands. Orange only on primary CTA.
   Borderless fill tiles, pill buttons, one idea per chapter.
   ───────────────────────────────────────────────────────────── */

/** Fade-up once on scroll. No-op under prefers-reduced-motion. */
export function Reveal({
  children,
  i: _i = 0,
  className,
  as = "div",
}: {
  children: ReactNode;
  i?: number;
  className?: string;
  as?: "div" | "section" | "li" | "span" | "h2";
}) {
  const Tag = as;
  void _i;
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      el.classList.add("reveal-visible");
      return;
    }
    el.classList.add("reveal-ready");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        el.classList.add("reveal-visible");
        el.classList.remove("reveal-ready");
        io.disconnect();
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag ref={ref as never} className={className}>
      {children}
    </Tag>
  );
}

/** Full-page shell: surface, text color, navbar + footer. */
export function PageShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "min-h-dvh bg-background text-foreground selection:bg-gerdoo-500/20",
        className,
      )}
    >
      <Navbar />
      <main>{children}</main>
      <Footer />
    </div>
  );
}

/** Small accent label that sits above a heading — neutral grey, no orange. */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "text-footnote font-semibold tracking-wide text-muted-foreground",
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Pill / badge — rounded-full, neutral. */
export function Pill({
  children,
  icon: Icon,
  className,
}: {
  children: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-full bg-muted px-3.5 py-1.5 text-xs font-semibold text-muted-foreground",
        className,
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {children}
    </span>
  );
}

/** Consistent page header — centered, display type, no bottom border. */
export function PageHero({
  badge,
  badgeIcon,
  title,
  subtitle,
  align = "center",
  art,
  illustrated: illustratedProp,
  children,
}: {
  badge?: string;
  badgeIcon?: ComponentType<{ className?: string }>;
  title: ReactNode;
  subtitle?: ReactNode;
  align?: "center" | "start";
  /** Manuscript illustration URL. Switches to the illustrated hero used by the
   *  product pages: full height, text held to a start-aligned column on lg,
   *  the art pinned to the viewport's left edge. */
  art?: string;
  /** Use the illustrated hero even before the artwork exists, so the layout
   *  settles once instead of shifting when the file lands. Defaults to `!!art`. */
  illustrated?: boolean;
  children?: ReactNode;
}) {
  const BadgeIcon = badgeIcon;
  const illustrated = illustratedProp ?? !!art;
  const centered = align === "center" && !illustrated;
  return (
    <section
      className={cn(
        "relative overflow-hidden",
        illustrated ? "flex min-h-svh items-center pb-12 pt-24 sm:pb-14 sm:pt-28" : "pt-28 pb-16 sm:pt-36 sm:pb-20",
      )}
    >
      {/* Background inside the lg breakpoint via a custom property, so phones
          never fetch the file; inset top and bottom so `contain` keeps the art
          clear of the fixed navbar and the fold (as in product-page.tsx). */}
      {art && (
        <div
          aria-hidden
          style={{ "--hero-art": `url('${art}')` } as React.CSSProperties}
          className="pointer-events-none absolute bottom-8 left-0 top-16 hidden w-[38vw] max-w-[560px] bg-contain bg-left bg-no-repeat lg:block lg:bg-[image:var(--hero-art)]"
        />
      )}
      <div
        className={cn(
          "relative mx-auto w-full px-8 sm:px-12",
          illustrated ? "max-w-[1180px]" : "max-w-[980px]",
          centered ? "text-center" : "text-start",
        )}
      >
        <div className={illustrated ? "lg:w-[56%]" : undefined}>
          {badge && (
            <Reveal i={0}>
              {/* An illustrated hero is on the flat system, so its badge is the
                  icon chip the product and marketplace pages use. Pages still
                  on the older look keep the pill. */}
              {illustrated ? (
                <span className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-gerdoo-50 text-gerdoo-600 ring-1 ring-inset ring-gerdoo-500/15">
                    {BadgeIcon && <BadgeIcon className="h-5 w-5" aria-hidden />}
                  </span>
                  <span className="text-[13px] font-semibold text-ink-500">{badge}</span>
                </span>
              ) : (
                <Pill icon={badgeIcon}>{badge}</Pill>
              )}
            </Reveal>
          )}
          <Reveal i={1}>
            <h1
              className={cn(
                "mt-6 text-balance text-[2rem] font-extrabold leading-[1.5] tracking-tight sm:text-[2.5rem] sm:leading-[1.45] md:text-display md:leading-[1.45]",
                centered && "mx-auto max-w-4xl",
              )}
            >
              {title}
            </h1>
          </Reveal>
          {subtitle && (
            <Reveal i={2}>
              <p
                className={cn(
                  "mt-5 text-body leading-relaxed text-muted-foreground sm:text-xl sm:leading-relaxed",
                  centered && "mx-auto max-w-2xl",
                )}
              >
                {subtitle}
              </p>
            </Reveal>
          )}
          {children && (
            <Reveal i={3}>
              <div
                className={cn(
                  "mt-9 flex flex-col gap-3 sm:flex-row",
                  centered ? "items-center justify-center" : "items-stretch sm:items-center",
                )}
              >
                {children}
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

type SectionTone = "white" | "canvas" | "dark";

/** Section wrapper. `muted`/`bordered` kept as aliases for callers. */
export function Section({
  children,
  muted = false,
  bordered = false,
  tone,
  className,
}: {
  children: ReactNode;
  muted?: boolean;
  bordered?: boolean;
  tone?: SectionTone;
  className?: string;
}) {
  void bordered;
  const resolved: SectionTone = tone ?? (muted ? "canvas" : "white");
  return (
    <section
      className={cn(
        "py-24 sm:py-32",
        resolved === "canvas" && "bg-muted",
        resolved === "dark" && "band-dark",
        className,
      )}
    >
      <div className="mx-auto max-w-[1200px] px-8 sm:px-12">{children}</div>
    </section>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  align = "center",
  className,
}: {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  align?: "center" | "start";
  className?: string;
}) {
  const centered = align === "center";
  return (
    <Reveal
      className={cn(
        "mb-14 sm:mb-16",
        centered ? "mx-auto max-w-[680px] text-center" : "max-w-[680px] text-start",
        className,
      )}
    >
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="mt-2 text-[1.75rem] font-extrabold leading-[1.5] tracking-tight sm:text-[2.25rem] sm:leading-[1.45] md:text-[2.5rem] md:leading-[1.45]">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{subtitle}</p>
      )}
    </Reveal>
  );
}

/** Flat fill tile. No border, no shadow. */
export function Card({
  children,
  className,
  href,
  hover = false,
  i,
}: {
  children: ReactNode;
  className?: string;
  href?: string;
  hover?: boolean;
  i?: number;
}) {
  const base = cn(
    "rounded-tile bg-muted p-8",
    hover && "transition-opacity hover:opacity-90",
    className,
  );
  const body = href ? (
    <Link to={href} className={cn("block h-full", base)}>
      {children}
    </Link>
  ) : (
    <div className={base}>{children}</div>
  );
  return i === undefined ? body : <Reveal i={i} className="h-full">{body}</Reveal>;
}

/** Square accent chip holding an icon — fill only. */
export function IconChip({
  icon: Icon,
  className,
  size = "md",
}: {
  icon: ComponentType<{ className?: string }>;
  className?: string;
  size?: "md" | "lg";
}) {
  return (
    <span
      className={cn(
        "flex items-center justify-center rounded-full bg-background text-muted-foreground",
        size === "lg" ? "h-12 w-12" : "h-10 w-10",
        className,
      )}
    >
      <Icon className={size === "lg" ? "h-6 w-6" : "h-5 w-5"} />
    </span>
  );
}

/** Standard feature card: distinct bordered card, primary icon chip, title, description, optional link. */
export function FeatureCard({
  icon: Icon,
  title,
  desc,
  href,
  i = 0,
  cta = "مشاهده جزئیات",
  center = true,
}: {
  icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  desc: ReactNode;
  href?: string;
  i?: number;
  cta?: string;
  center?: boolean;
}) {
  const content = (
    <div
      className={cn(
        "group relative flex h-full flex-col justify-between rounded-2xl border border-border/80 bg-card p-6 sm:p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/50 hover:shadow-md hover:shadow-primary/5 dark:bg-card dark:border-border/60",
        center ? "items-center text-center" : "items-start text-start",
      )}
    >
      <div className={cn("flex flex-col w-full", center ? "items-center text-center" : "items-start text-start")}>
        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
          <Icon className="h-6 w-6" />
        </div>
        <h3 className="text-lg font-bold tracking-tight text-foreground transition-colors duration-200 group-hover:text-primary">
          {title}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {desc}
        </p>
      </div>
      {href && (
        <span className="learn-more mt-6">
          {cta}
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </span>
      )}
    </div>
  );

  const wrapped = href ? (
    <Link to={href} className="block h-full">
      {content}
    </Link>
  ) : (
    content
  );

  return i === undefined ? wrapped : <Reveal i={i} className="h-full">{wrapped}</Reveal>;
}

export function Stat({ value, label }: { value: ReactNode; label: ReactNode }) {
  return (
    <div>
      <div className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
        {value}
      </div>
      <p className="mt-1.5 text-footnote text-muted-foreground">{label}</p>
    </div>
  );
}

/** Checklist row. */
export function CheckItem({ children, i = 0 }: { children: ReactNode; i?: number }) {
  return (
    <Reveal as="li" i={i} className="flex items-start gap-3">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
        <Check className="h-3.5 w-3.5" />
      </span>
      <span className="text-foreground/90">{children}</span>
    </Reveal>
  );
}

/** Header for document-style pages (legal, policies). */
export function DocHeader({
  icon: Icon,
  title,
  meta,
}: {
  icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <section className="pb-10 pt-28 sm:pb-12 sm:pt-32">
      <div className="mx-auto max-w-[760px] px-6 text-start">
        <Reveal>
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-gerdoo-50 text-gerdoo-600 ring-1 ring-inset ring-gerdoo-500/15">
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <h1 className="mt-6 text-balance text-[1.75rem] font-extrabold leading-[1.35] tracking-[-0.025em] text-ink-950 sm:text-[2.25rem] sm:leading-[1.3]">
            {title}
          </h1>
          {meta && <p className="mt-4 text-[13px] text-ink-500">{meta}</p>}
        </Reveal>
      </div>
    </section>
  );
}

/** Fill panel used to group legal/doc subsections. */
export function DocSection({
  icon: Icon,
  title,
  children,
}: {
  icon?: ComponentType<{ className?: string }>;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <Reveal className="border-t border-black/[0.08] pt-9">
      <h2 className="flex items-center gap-2.5 text-[17px] font-bold leading-[1.7] tracking-tight text-ink-950">
        {Icon && <Icon className="h-4.5 w-4.5 shrink-0 text-gerdoo-600" aria-hidden />}
        <span>{title}</span>
      </h2>
      <div className="mt-4 text-[15px] leading-[2] text-ink-600 [&_a]:font-bold [&_a]:text-gerdoo-700 [&_strong]:text-ink-900">
        {children}
      </div>
    </Reveal>
  );
}

/** FAQ list — Apple hairline stack. */
export function FaqSection({
  title = "سوالات متداول",
  items,
}: {
  title?: string;
  items: { q: string; a: ReactNode }[];
}) {
  return (
    <Section>
      <div className="mx-auto max-w-[680px]">
        <h2 className="mb-10 text-center text-[1.75rem] font-extrabold leading-[1.5] tracking-tight sm:text-[2rem] sm:leading-[1.45]">
          {title}
        </h2>
        <div className="divide-y divide-border text-start">
          {items.map((f, i) => (
            <Reveal key={typeof f.q === "string" ? f.q : i} i={i}>
              <div className="py-6">
                <h3 className="font-bold text-foreground">{f.q}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
}

/** SEO / long-form prose panel — borderless canvas fill. */
export function ProsePanel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Section className={className}>
      <Reveal className="mx-auto max-w-[980px]">
        <div
          className={cn(
            "rounded-tile bg-muted p-8 text-start sm:p-12",
            "prose prose-neutral prose-lg mx-auto max-w-none dark:prose-invert",
            "prose-headings:font-extrabold prose-headings:tracking-tight prose-headings:text-foreground",
            "prose-p:text-muted-foreground prose-p:leading-relaxed",
            "prose-li:text-muted-foreground",
            "prose-strong:text-foreground",
            "prose-a:font-semibold prose-a:text-foreground prose-a:no-underline hover:prose-a:opacity-70",
          )}
        >
          {children}
        </div>
      </Reveal>
    </Section>
  );
}

/**
 * Shared marketing action control — pill shape.
 * Primary is the only orange element on the page.
 */
export const actionControl =
  "box-border inline-flex h-11 min-h-11 max-h-11 shrink-0 items-center justify-center gap-2 overflow-hidden rounded-md border-0 px-6 text-sm font-bold leading-none no-underline transition-opacity hover:opacity-90";

export function actionPrimary(className?: string) {
  return cn(
    actionControl,
    "bg-gerdoo-600 text-white hover:bg-gerdoo-500",
    className,
  );
}

/** Secondary on light surfaces: solid muted fill. */
export function actionOutline(className?: string) {
  return cn(
    actionControl,
    "bg-muted text-foreground hover:opacity-80",
    className,
  );
}

/** Secondary on dark CTA bands. */
export function actionOutlineOnDark(className?: string) {
  return cn(
    actionControl,
    "bg-white/10 text-white hover:bg-white/15",
    className,
  );
}

/** Apple end-of-page CTA — centered, no box. */
export function CTA({
  title,
  subtitle,
  primaryHref,
  primaryLabel,
  secondaryTo,
  secondaryLabel,
}: {
  title: ReactNode;
  subtitle: ReactNode;
  primaryHref: string;
  primaryLabel: string;
  secondaryTo?: string;
  secondaryLabel?: string;
}) {
  return (
    <Section tone="canvas">
      <Reveal className="mx-auto max-w-[680px] text-center">
        <h2 className="text-[1.75rem] font-extrabold leading-[1.5] tracking-tight text-foreground sm:text-[2.25rem] sm:leading-[1.45] md:text-[2.5rem] md:leading-[1.45]">
          {title}
        </h2>
        <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{subtitle}</p>
        <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <a href={primaryHref} className={actionPrimary()}>
            {primaryLabel}
          </a>
          {secondaryTo && secondaryLabel && (
            secondaryTo.startsWith("mailto:") || secondaryTo.startsWith("http") ? (
              <a href={secondaryTo} className="learn-more">
                {secondaryLabel}
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </a>
            ) : (
              <Link to={secondaryTo} className="learn-more">
                {secondaryLabel}
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </Link>
            )
          )}
        </div>
      </Reveal>
    </Section>
  );
}

/** Apple "Learn more ›" text link (RTL chevron). */
export function LearnMore({
  to,
  href,
  children = "بیشتر بدانید",
  className,
}: {
  to?: string;
  href?: string;
  children?: ReactNode;
  className?: string;
}) {
  const cls = cn("learn-more", className);
  const inner = (
    <>
      {children}
      <ChevronLeft className="h-4 w-4" aria-hidden />
    </>
  );
  if (href) return <a href={href} className={cls}>{inner}</a>;
  if (to) return <Link to={to} className={cls}>{inner}</Link>;
  return <span className={cls}>{inner}</span>;
}

// Re-export ArrowLeft for callers that still import it via page barrel patterns.
export { ArrowLeft };
